/**
 * Connectivity tracking for offline session support.
 *
 * Tracks the browser's `navigator.onLine` state plus a `simulatedOffline`
 * override so the offline experience can be exercised from the super admin
 * test area without needing devtools network throttling.
 *
 * This module intentionally has no React dependency — `useConnectivity.ts`
 * wraps it for components via `useSyncExternalStore`.
 */

type ConnectivityListener = () => void;

const listeners = new Set<ConnectivityListener>();

let simulatedOffline = false;

const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', notifyListeners);
  window.addEventListener('offline', notifyListeners);
}

/**
 * True when the app should behave as offline — either the browser reports
 * no connection, or offline mode has been manually simulated for testing.
 */
export const isEffectivelyOffline = (): boolean =>
  simulatedOffline || !navigator.onLine;

/**
 * True only when the browser itself reports no connection — ignores the
 * simulated-offline test override. Use this (not `isEffectivelyOffline`)
 * anywhere a real network signal is required, e.g. deciding whether a 401 is
 * trustworthy.
 */
export const isNavigatorOffline = (): boolean => !navigator.onLine;

/**
 * Manually force the app into (or out of) offline mode, independent of the
 * actual browser connectivity state. Used by the super admin offline test
 * area.
 */
export const setSimulatedOffline = (value: boolean): void => {
  if (simulatedOffline === value) return;
  simulatedOffline = value;
  notifyListeners();
};

export const isSimulatedOffline = (): boolean => simulatedOffline;

/**
 * Subscribe to connectivity changes (real or simulated). Returns an
 * unsubscribe function.
 */
export const subscribeConnectivity = (
  listener: ConnectivityListener
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
